import {css, Typography, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  ConfirmDialog,
  DlMuiDynamicIcon,
  ExportUtils,
  GetStylesFunction,
  omitNullsy,
  useApiQuery,
  useCurrentLocale,
} from '@wallaby-ui/core';
import {useFormatters} from '@wallaby-ui/document';
import {useCallback} from 'react';
import * as React from 'react';

import {InterventionsDepartmentReportFilter} from './departmenttypes';

export function useDepartmentReportExport(
  filters: InterventionsDepartmentReportFilter,
  config: ConfigObjectType,
  totalCount: number | undefined
) {
  const currentLocale = useCurrentLocale();
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {t} = useFormatters();

  const apiUrl = config.service.url.maxExportRows;

  const {data: maxExportedRows} = useApiQuery<number | undefined>([apiUrl], null, {
    axiosConfig: {
      url: apiUrl,
    },
    reactQueryOptions: {
      enabled: true,
    },
  });

  const downloadXlsxExport = useCallback(() => {
    ExportUtils.handleExportXlsx({
      url: config.service.url.interventionDepartmentExport,
      filters: omitNullsy({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        measureType: filters.measureType,
        organizationIds: filters.organizationIds,
        atcHierarchyIds: filters.atcHierarchyIds,
        riskCategories: filters.riskCategories,
        types: filters.types,
        manifestations: filters.manifestations,
        causes: filters.causes,
        causesAggregationType: filters.causesAggregationType,
        plannedInterventions: filters.plannedInterventions,
        language: currentLocale.toUpperCase(),
      }),
      locale: currentLocale,
    });
  }, [
    config.service.url.interventionDepartmentExport,
    currentLocale,
    filters.atcHierarchyIds,
    filters.causes,
    filters.causesAggregationType,
    filters.dateFrom,
    filters.dateTo,
    filters.manifestations,
    filters.measureType,
    filters.organizationIds,
    filters.plannedInterventions,
    filters.riskCategories,
    filters.types,
  ]);

  const [confirmExportWarningOpened, setConfirmExportWarningOpened] = React.useState(false);
  const onConfirmExportWarning = useCallback(() => {
    setConfirmExportWarningOpened(false);
    downloadXlsxExport();
  }, [downloadXlsxExport]);

  const isWarningDialogVisible = useCallback(() => {
    if (!maxExportedRows) return;
    return totalCount != null && totalCount > maxExportedRows;
  }, [totalCount, maxExportedRows]);

  const handleXlsxExport = useCallback(() => {
    if (isWarningDialogVisible()) {
      setConfirmExportWarningOpened(true);
    } else {
      downloadXlsxExport();
    }
  }, [isWarningDialogVisible, downloadXlsxExport]);

  const handleCancel = useCallback(() => {
    // noop
  }, []);

  const warningDialog = isWarningDialogVisible() ? (
    <ConfirmDialog
      isOpened={confirmExportWarningOpened}
      handleConfirm={onConfirmExportWarning}
      handleCancel={handleCancel}
      showCancel={false}
      titleIcon={
        <DlMuiDynamicIcon
          iconName='haidiWarningIcon'
          useFixedViewBox='0 0 13 12'
          css={muiStyles.confirmIconButton}
          svgIconInputCss={muiStyles.confirmIcon}
          noButtonStyles
        />
      }
      titleText={t('common.export.confirmDialogLimit.title')}
      confirmText={t('common.confirmDialog.accept')}
    >
      <Typography variant='value'>{t('common.export.confirmDialogLimit.text')}</Typography>
    </ConfirmDialog>
  ) : undefined;

  return {handleXlsxExport, warningDialog};
}

const getStyles: GetStylesFunction = (options) => ({
  confirmIconButton: css`
    color: var(--dlTheme-500);
    padding: 0;
  `,
  confirmIcon: css`
    font-size: 20px;
  `,
});
